// components/achievements/AchievementsTab.jsx
// Milestone Achievements Tab for EarnPage
// ============================================
// UPDATED: Dec 31, 2025 - Added defensive date validation (B3 fix)

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { 
  Trophy, 
  CheckCircle, 
  ChevronRight, 
  ChevronDown,
  Coins,
  Loader2,
  RefreshCw,
  Lock,
  Sparkles
} from 'lucide-react';
import { useTokens } from '../../context/TokenContext';
import { useNotification } from '../../context/NotificationContext';
import { useAchievements } from '../../context/AchievementContext';

// GraphQL Queries
const GET_USER_ACHIEVEMENTS = gql`
  query GetUserAchievements {
    userAchievements {
      achieved {
        id
        name
        description
        icon
        tokens
        category
        achievedAt
      }
      available {
        id
        name
        description
        icon
        tokens
        category
      }
      totalTokensEarned
      totalAchieved
      totalAvailable
      percentComplete
    }
  }
`;

const GET_MILESTONE_CATEGORIES = gql`
  query GetMilestoneCategories {
    milestoneCategories {
      id
      name
      description
      icon
      order
      milestones {
        id
        name
        description
        icon
        tokens
      }
    }
  }
`;

const CHECK_MILESTONES = gql`
  mutation CheckMilestones {
    checkMilestones {
      newlyAchieved {
        id
        name
        description
        icon
        tokens
      }
      totalChecked
      totalAchieved
      totalNewTokens
    }
  }
`;

const RECORD_ACTIVITY = gql`
  mutation RecordActivity {
    recordActivity {
      streakUpdated
      newlyAchieved {
        id
        name
        icon
        tokens
      }
    }
  }
`;

// Category display names and icons
const categoryMeta = {
  GETTING_STARTED: { name: 'Getting Started', icon: '🚀' },
  SHOPPING: { name: 'Shopping', icon: '🛒' },
  SAVINGS: { name: 'Savings', icon: '💰' },
  REFERRALS: { name: 'Referrals', icon: '🤝' },
  JOBS: { name: 'Jobs & Contributions', icon: '💼' },
  LOYALTY: { name: 'Loyalty', icon: '❤️' },
  TOKENS: { name: 'Token Economy', icon: '🪙' },
  TRUST: { name: 'Trust & Reputation', icon: '⭐' },
  GROUPS: { name: 'Groups & Community', icon: '👥' }
};

// ============================================
// HELPER: Safe date formatting (B3 FIX)
// Validates date before formatting to prevent "Invalid Date"
// ============================================
const formatAchievementDate = (dateValue) => {
  // Handle null/undefined
  if (!dateValue) return null;
  
  // Try to parse the date
  const date = new Date(dateValue);
  
  // Check if date is valid
  // Invalid dates return NaN for getTime()
  if (isNaN(date.getTime())) {
    console.warn('[AchievementsTab] Invalid date value:', dateValue);
    return null;
  }
  
  // Also check for unreasonable dates (before 2020 or in far future)
  const year = date.getFullYear();
  if (year < 2020 || year > 2100) {
    console.warn('[AchievementsTab] Date out of reasonable range:', dateValue);
    return null;
  }
  
  // Format the valid date
  try {
    return date.toLocaleDateString();
  } catch (err) {
    console.warn('[AchievementsTab] Date formatting error:', err);
    return null;
  }
};

const AchievementsTab = () => {
  const { refreshBalance } = useTokens();
  const { addNotification } = useNotification();
  const { celebrateAchievements } = useAchievements();
  
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Fetch user achievements
  const { 
    data: achievementsData, 
    loading: achievementsLoading, 
    refetch: refetchAchievements 
  } = useQuery(GET_USER_ACHIEVEMENTS, {
    fetchPolicy: 'cache-and-network'
  });
  
  // Fetch all categories
  const { 
    data: categoriesData, 
    loading: categoriesLoading 
  } = useQuery(GET_MILESTONE_CATEGORIES);
  
  // Check milestones mutation - NOW USES GLOBAL CELEBRATION
  const [checkMilestones, { loading: checking }] = useMutation(CHECK_MILESTONES, {
    onCompleted: (data) => {
      if (data.checkMilestones.newlyAchieved?.length > 0) {
        // Use global celebration system!
        celebrateAchievements(data.checkMilestones.newlyAchieved);
        refreshBalance?.();
        refetchAchievements();
      } else {
        addNotification?.({
          type: 'info',
          title: 'All Caught Up!',
          message: 'No new achievements to claim right now.',
          showToast: true
        });
      }
    },
    onError: (error) => {
      addNotification?.({
        type: 'error',
        title: 'Error',
        message: error.message,
        showToast: true
      });
    }
  });
  
  // Record activity mutation
  const [recordActivity] = useMutation(RECORD_ACTIVITY);
  
  // Record activity on mount
  useEffect(() => {
    recordActivity().catch(console.error);
  }, [recordActivity]);
  
  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };
  
  // Check if milestone is achieved
  const isAchieved = (milestoneId) => {
    return achievementsData?.userAchievements?.achieved?.some(a => a.id === milestoneId);
  };
  
  // Get achievement date - NOW USES SAFE FORMATTING (B3 FIX)
  const getAchievementDate = (milestoneId) => {
    const achievement = achievementsData?.userAchievements?.achieved?.find(a => a.id === milestoneId);
    if (achievement?.achievedAt) {
      return formatAchievementDate(achievement.achievedAt);
    }
    return null;
  };
  
  const achievements = achievementsData?.userAchievements;
  const categories = categoriesData?.milestoneCategories || [];
  
  // Calculate category progress
  const getCategoryProgress = (categoryId) => {
    const categoryMilestones = categories.find(c => c.id === categoryId)?.milestones || [];
    const achieved = categoryMilestones.filter(m => isAchieved(m.id)).length;
    return { achieved, total: categoryMilestones.length };
  };


  
  if (achievementsLoading && !achievements) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Progress Overview Card */}
      <div className="bg-gradient-to-br from-purple-600 to-indigo-700 text-white rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-white/80 text-sm">Your Progress</p>
            <p className="text-3xl font-bold">
              {achievements?.totalAchieved || 0}
              <span className="text-lg text-white/60">/{achievements?.totalAvailable || 0}</span>
            </p>
          </div>
          <div className="text-right">
            <p className="text-white/80 text-sm">Tokens Earned</p>
            <p className="text-2xl font-bold flex items-center justify-end gap-1">
              <Coins className="w-5 h-5" />
              {achievements?.totalTokensEarned || 0}
            </p>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="w-full bg-white/20 rounded-full h-3 mb-2">
          <div 
            className="bg-yellow-400 h-3 rounded-full transition-all duration-500"
            style={{ width: `${achievements?.percentComplete || 0}%` }}
          />
        </div>
        <p className="text-sm text-white/80 text-center">
          {achievements?.percentComplete || 0}% Complete
        </p>
      </div>
      
      {/* Check for New Achievements Button */}
      <button
        onClick={() => checkMilestones()}
        disabled={checking}
        className="w-full bg-orange-500 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-orange-600 transition-colors disabled:opacity-50"
      >
        {checking ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Checking...
          </>
        ) : (
          <>
            <RefreshCw className="w-5 h-5" />
            Check for New Achievements
          </>
        )}
      </button>


      
      {/* Recent Achievements */}
      {achievements?.achieved?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-yellow-500" />
            Recently Earned
          </h3>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {achievements.achieved.slice(0, 5).map(achievement => (
              <div 
                key={achievement.id}
                className="flex-shrink-0 bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-3 w-28 text-center"
              >
                <span className="text-2xl">{achievement.icon}</span>
                <p className="text-xs font-medium text-gray-900 mt-1 truncate">
                  {achievement.name}
                </p>
                <p className="text-xs text-yellow-600 flex items-center justify-center gap-0.5 mt-0.5">
                  <Coins className="w-3 h-3" />
                  +{achievement.tokens}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Categories List */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-gray-700">All Milestones</h3>
        
        {categories.map(category => {
          const progress = getCategoryProgress(category.id);
          const isExpanded = expandedCategories[category.id];
          const isComplete = progress.achieved === progress.total;
          const meta = categoryMeta[category.id] || { name: category.name, icon: category.icon };
          
          return (
            <div key={category.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category.id)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{meta.icon}</span>
                  <div className="text-left">
                    <h4 className="font-medium text-gray-900 text-sm">{meta.name}</h4>
                    <div className="flex items-center gap-2">
                      <div className="w-20 bg-gray-200 rounded-full h-1.5">
                        <div 
                          className={`h-1.5 rounded-full ${isComplete ? 'bg-green-500' : 'bg-orange-500'}`}
                          style={{ width: `${(progress.achieved / progress.total) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500">
                        {progress.achieved}/{progress.total}
                      </span>
                    </div>
                  </div>
                </div>
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
              </button>
              
              {/* Category Milestones */}
              {isExpanded && (
                <div className="border-t border-gray-100 bg-gray-50">
                  {category.milestones.map((milestone, index) => {
                    const achieved = isAchieved(milestone.id);
                    const achievedDate = getAchievementDate(milestone.id);
                    
                    return (
                      <div 
                        key={milestone.id}
                        className={`px-4 py-2.5 flex items-center gap-3 ${
                          index !== category.milestones.length - 1 ? 'border-b border-gray-100' : ''
                        } ${achieved ? 'bg-green-50/50' : ''}`}
                      >
                        {/* Icon */}
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-lg ${
                          achieved 
                            ? 'bg-green-100' 
                            : 'bg-gray-200 grayscale opacity-50'
                        }`}>
                          {achieved ? milestone.icon : <Lock className="w-4 h-4 text-gray-400" />}
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <h5 className={`text-sm font-medium ${achieved ? 'text-gray-900' : 'text-gray-500'}`}>
                              {milestone.name}
                            </h5>
                            {achieved && (
                              <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                            )}
                          </div>
                          <p className={`text-xs ${achieved ? 'text-gray-600' : 'text-gray-400'}`}>
                            {milestone.description}
                          </p>
                          {/* B3 FIX: Only show date if it's valid */}
                          {achieved && achievedDate && (
                            <p className="text-xs text-green-600">
                              Earned {achievedDate}
                            </p>
                          )}
                        </div>
                        
                        {/* Tokens */}
                        <div className={`flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs ${
                          achieved 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-200 text-gray-500'
                        }`}>
                          <Coins className="w-3 h-3" />
                          <span className="font-medium">{milestone.tokens}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AchievementsTab;